import { isWild, isValue, type Card, type ValueCard, type WildCard } from "../types/card";
import type {
  ApplyMoveResult,
  BonusStackChoice,
  EngineEvent,
  GameState,
  GuessType,
  PlayerMove,
  PlayerState,
  StackIndex,
  SwapChoice,
  TableStack,
} from "../types/game";
import { isTurnInProgress, STACK_INDICES } from "../types/game";
import {
  allowedStackIndices,
  blueShellTargetIds,
  canTargetPlayer,
  canUseStreak,
  enabledStarfallEvents,
  locksAfterTargeting,
  lockedStackIndices,
  MAX_STREAK_LOCKS,
  normalizeHouseRules,
  pendingLockCount,
  smallestOpenStackIndices,
  streakSpendApplies,
  streaksApply,
  type StreakTarget,
} from "./houseRules";
import { matchDistance } from "./matchDistance";
import type { RNG } from "./rng";
import {
  activeStarfall,
  bountyTurns,
  chargeStarfall,
  COMET_CARDS,
  drawStarfallEvent,
  isBountyStack,
  MAX_STARFALL_EVENTS_PER_TURN,
  meteorStrike,
  meteorSwarm,
  pickBountyStacks,
  rebaseStarfall,
  redealStorm,
  STARFALL_METER_SIZE,
  starfallShuffle,
  stormLayout,
} from "./starfall";
import type { StarfallState } from "../types/game";
import { computeRoundScores } from "./scoring";
import { MERCY_COOLDOWN_TURNS, mercyApplies, mercyBurstSize, mercyPiles } from "./mercy";
import { activeBurningDeck, canBurnFrom, isBurnStreak } from "./burningDeck";
import { activeDrought, DROUGHT_MISSES_TO_MIST, droughtPiles, droughtSplit, isMisted } from "./drought";

export class IllegalMoveError extends Error {}

function clone(state: GameState): GameState {
  // Structured clone keeps this simple + correct; game state is small (a couple hundred cards max).
  return structuredClone(state);
}

function topOf(stack: TableStack): Card | undefined {
  return stack.cards[0];
}

function stackAllowsFree(stack: TableStack): boolean {
  const top = topOf(stack);
  return top === undefined || isWild(top);
}

function isBoundaryInvalid(guess: GuessType, topValue: number): boolean {
  if (guess === "higher" && topValue === 13) return true;
  if (guess === "lower" && topValue === 1) return true;
  return false;
}

function currentPlayer(state: GameState): PlayerState {
  return state.players[state.currentPlayerIndex];
}

function drawTop(player: PlayerState): Card {
  const card = player.deck.shift();
  if (!card) {
    throw new IllegalMoveError(`player ${player.id} has no cards to play`);
  }
  return card;
}

function placeOnStack(stack: TableStack, card: Card): void {
  stack.cards.unshift(card);
}

/**
 * Smash Card: the just-placed wild removes every VALUE card beneath it from play
 * entirely; the wild itself remains on top of the stack (where, as usual, it
 * acts like an empty stack for free placement). `cardCount` reports the
 * destroyed cards, which drives the client's slam count (one slam per 5 cards).
 *
 * Wilds beneath survive the slam and stay in the pile, in order (see "A wild is
 * never destroyed"): a Smash landing on a pile whose every card is a wild
 * therefore destroys nothing and still slams, because the card was still spent.
 */
function annihilateStack(state: GameState, player: PlayerState, stackIndex: StackIndex, events: EngineEvent[]): void {
  const stack = state.stacks[stackIndex];
  const beneath = stack.cards.slice(1);
  const survivors = beneath.filter(isWild);
  const cardCount = beneath.length - survivors.length;
  stack.cards = [...stack.cards.slice(0, 1), ...survivors]; // the wild stays on top, and so does any wild it landed on
  events.push({ type: "wild-annihilation", playerId: player.id, stackIndex, cardCount });
}

/**
 * Lightning Card: bolts arc out to the other four stacks and vaporize the top
 * card of each (removed from play). The Lightning's own stack is untouched — it
 * simply rests on top of whatever was already there, acting as an empty stack
 * for free placement like any wild.
 *
 * A pile showing a WILD keeps it (see "A wild is never destroyed"): the bolt
 * still flies, and the target is reported with `resisted` so the client can play
 * the strike glancing off instead of nothing happening to that pile at all.
 */
function lightningStrike(state: GameState, player: PlayerState, stackIndex: StackIndex, events: EngineEvent[]): void {
  const targets: { stackIndex: StackIndex; card: Card; revealed: Card | null; resisted?: boolean }[] = [];
  for (const idx of STACK_INDICES) {
    if (idx === stackIndex) continue;
    const target = state.stacks[idx];
    const top = target.cards[0];
    if (!top) continue;
    if (isWild(top)) {
      // Nothing moves: the wild is still the top card, so it uncovers nothing.
      targets.push({ stackIndex: idx, card: top, revealed: null, resisted: true });
      continue;
    }
    target.cards.shift();
    // `revealed` is whatever the strike uncovered, so the client can slot it in
    // underneath before the vaporized card fades out.
    targets.push({ stackIndex: idx, card: top, revealed: target.cards[0] ?? null });
  }
  events.push({ type: "lightning-strike", playerId: player.id, stackIndex, targets });
}

/**
 * Swap Card: owes its player one swap of two piles' top cards, made at the END
 * of the turn (`parkForOwedSwaps`), not now.
 *
 * Deferred because the turn is still moving cards: a swap made the instant the
 * Swap landed would be chosen against a table the rest of the turn — the Swap's
 * own follow-up card, a chain, the rest of a Five Out — immediately rearranges.
 * At the end of the turn the table holds still, and the choice is about the
 * board the next player will actually face.
 */
function oweSwap(state: GameState, player: PlayerState, stackIndex: StackIndex, events: EngineEvent[]): void {
  state.swapsOwed = (state.swapsOwed ?? 0) + 1;
  events.push({ type: "swap-owed", playerId: player.id, stackIndex });
}

/**
 * Peek Card: its player will see their own next card until they play it (see
 * `PlayerState.peeking` and `visiblePeekCard`). It changes nothing on the table.
 */
function activatePeek(player: PlayerState, stackIndex: StackIndex, events: EngineEvent[]): void {
  player.peeking = true;
  events.push({ type: "peek-activated", playerId: player.id, stackIndex });
}

/**
 * Fires a wild the moment it lands, whichever kind it is, then covers it with
 * the follow-up card the rules give its player.
 *
 * Both halves happen no matter how the wild was played — guess, duplicate/chain,
 * free placement, Five Out bonus, or off-by-one bonus. The follow-up is what
 * makes a wild worth holding: the player who cleared the pile is the one who
 * gets to restart it, rather than leaving a wild-topped (i.e. free) pile sitting
 * there for whoever plays next.
 *
 * The follow-up card is never evaluated, so it simply stays. If it is itself a
 * wild it fires in turn and takes a follow-up of its own — the loop, not
 * recursion, because a deck of wilds would otherwise be a stack depth.
 *
 * An empty deck ends it: the player is out, and every call site runs
 * `checkWentOut` immediately afterwards.
 */
function resolveWild(state: GameState, player: PlayerState, stackIndex: StackIndex, card: WildCard, events: EngineEvent[]): void {
  let wild = card;
  while (true) {
    switch (wild.wildKind) {
      case "lightning":
        lightningStrike(state, player, stackIndex, events);
        break;
      case "smash":
        annihilateStack(state, player, stackIndex, events);
        break;
      case "swap":
        oweSwap(state, player, stackIndex, events);
        break;
      case "peek":
        activatePeek(player, stackIndex, events);
        break;
    }
    // A strike can expose a triplet, and the wild itself hides whatever it covered.
    chargeStarfall(state, player.id, events);

    if (player.deck.length === 0) return;
    const followUp = drawTop(player);
    placeOnStack(state.stacks[stackIndex], followUp);
    events.push({ type: "wild-follow-up-card", playerId: player.id, stackIndex, card: followUp });
    chargeStarfall(state, player.id, events);
    if (!isWild(followUp)) return;
    wild = followUp; // a wild covering a wild fires too, and is covered in turn
  }
}

/**
 * The card the taking player lays into the gap the taken pile left, before their
 * turn ends. Unevaluated like any free placement, and a wild fires (and is
 * covered) as usual.
 *
 * This can never win the round, which is why the design doc still says an
 * incorrect guess is not a win: the pile comes back carrying at least the two
 * VALUE cards that decided it — the one guessed against and the one played —
 * and they land at the very bottom of the deck. A refill only keeps drawing
 * while it turns up wilds, so it always stops on one of those two. The empty
 * check is for a hand-built state, not a reachable position.
 */
function refillTakenStack(state: GameState, player: PlayerState, stackIndex: StackIndex, events: EngineEvent[]): void {
  if (player.deck.length === 0) return;
  const card = drawTop(player);
  placeOnStack(state.stacks[stackIndex], card);
  events.push({ type: "stack-refill-card", playerId: player.id, stackIndex, card });
  if (isWild(card)) {
    resolveWild(state, player, stackIndex, card, events);
  } else {
    chargeStarfall(state, player.id, events);
  }
}

/**
 * Flips the taken stack as a single unit onto the bottom of the taking player's
 * deck (see design doc) - or, under a Drought mist, the half of it the mist
 * leaves (`mistStack`). Taking a pile is also what counts a Drought: one more
 * miss in a row, unless this take spent the mist.
 */
function takeStack(state: GameState, player: PlayerState, stackIndex: StackIndex, events: EngineEvent[]): void {
  const stack = state.stacks[stackIndex];
  const misted = mistStack(state, player, stackIndex, events);
  if (activeDrought(state)) player.drought = misted ? 0 : Math.min((player.drought ?? 0) + 1, DROUGHT_MISSES_TO_MIST);
  const takenTopToBottom = stack.cards.slice();
  const flipped = takenTopToBottom.slice().reverse();
  player.deck.push(...flipped);
  events.push({
    type: "stack-taken",
    playerId: player.id,
    stackIndex,
    cardCount: takenTopToBottom.length,
    revealedCards: takenTopToBottom,
  });
  stack.cards = [];
  // No star can come of an emptied pile; this records the triplet it may have broken.
  chargeStarfall(state, player.id, events);
}

/**
 * Drought's mist (see `engine/drought.ts`): a misted player taking a pile keeps
 * only its top half, and the lower half's value cards burst out onto the other
 * four piles before the take. Reports whether the mist was spent.
 *
 * The scattered cards land unevaluated, as comets do, but they are nobody's
 * play: all of them are value cards (`droughtSplit` sends wilds home), so
 * nothing fires and there is nothing to follow up. They land as one burst, and
 * the table it leaves is charged once - a triplet a later card covered in the
 * same burst was never on the table long enough to see.
 *
 * Nothing to scatter - a pile of two, or a lower half that is all wilds - does
 * not spend the mist, which is still there for the next miss.
 */
function mistStack(state: GameState, player: PlayerState, stackIndex: StackIndex, events: EngineEvent[]): boolean {
  const d = activeDrought(state);
  if (!d || !isMisted(player.drought)) return false;
  const stack = state.stacks[stackIndex];
  const { kept, scattered } = droughtSplit(stack.cards);
  if (scattered.length === 0) return false;
  stack.cards = kept;
  const piles = droughtPiles(d, stackIndex, scattered.length);
  const droughtCards = scattered.map((card, i) => ({ stackIndex: piles[i]!, card }));
  for (const { stackIndex: to, card } of droughtCards) placeOnStack(state.stacks[to], card);
  events.push({ type: "drought-mist", playerId: player.id, stackIndex, droughtCards });
  chargeStarfall(state, player.id, events);
  return true;
}

/**
 * Returns true if this player's deck is now empty — they are out. Must be called
 * after every single card leaves a player's deck, and every caller ends the turn
 * when it returns true: a player with no cards cannot carry on with whatever was
 * left of their move.
 *
 * Going out does NOT end the round, which is the whole of the equal-turns rule —
 * see `nextSeat`. All this records is who went out and when, so the announcement
 * still lands on the move that earned it rather than a lap later.
 *
 * The first player out keeps `winnerPlayerId`; anyone who goes out during the
 * last lap joins them in `roundWinnerIds`, and finishes level with them on cards,
 * so `computeRoundScores` already ties them on points with nothing to add.
 */
function checkWentOut(state: GameState, player: PlayerState, events: EngineEvent[]): boolean {
  if (player.deck.length > 0) return false;
  const alreadyOut = state.roundWinnerIds ?? [];
  if (alreadyOut.includes(player.id)) return true;
  if (state.winnerPlayerId === null) state.winnerPlayerId = player.id;
  state.roundWinnerIds = [...alreadyOut, player.id];
  events.push({ type: "player-won", playerId: player.id });
  return true;
}

/**
 * The seat that plays next, or null when the round is over.
 *
 * Everyone gets the same number of turns. A player going out therefore does not
 * end the round, it FIXES it: their turn count is the number this round will
 * have, and every player still holding cards who has had fewer plays one more,
 * in seat order. The round ends when nobody is short — which is immediately if
 * the player out was the last still owed a turn.
 *
 * The turn-count test is dormant until somebody is out, and has to be: while the
 * table is whole, the player about to receive it has always had exactly one
 * fewer turn than the player handing it over, so applying it would end every
 * round on the first lap.
 *
 * In a party the player who fixes the round need not be sitting HERE:
 * `partyLastLapTurns` is another table's first player out reaching this one, and
 * it arms the same test with the same number. Everything else about the rule is
 * unchanged, which is the point of expressing it as a turn count rather than as
 * a winner — a table can be in its last lap with nobody at it out.
 *
 * Players who are out are skipped rather than removed. A seat is where the
 * scores, the totals and every client's layout are keyed, and it holds their
 * empty deck for the rest of the round.
 */
function nextSeat(state: GameState): number | null {
  const seats = state.players.length;
  const partyLastLap = state.partyLastLapTurns ?? 0;
  const lastLap = state.winnerPlayerId !== null || partyLastLap > 0;
  const turnsInRound = Math.max(partyLastLap, ...state.players.map((p) => p.turnsTaken ?? 0));
  for (let i = 1; i <= seats; i++) {
    const index = (state.currentPlayerIndex + i) % seats;
    const player = state.players[index];
    if (player.deck.length === 0) continue; // out; they take no more turns
    if (lastLap && (player.turnsTaken ?? 0) >= turnsInRound) continue; // already had their share
    return index;
  }
  return null;
}

/**
 * Scores the round and ends it. At a single table it is only ever reached with
 * at least one player out, since a table where everybody still holds cards
 * always has a seat to play next — but in a party it is also reached with nobody
 * here out at all, when the table that fixed the round was another one and this
 * table's seats have already had their turns.
 */
function finishRound(state: GameState, events: EngineEvent[]): void {
  // Position scoring applies to every round, solo included (a solo player simply
  // scores 1). Solo rounds go straight to game-over and are graded on turn count.
  const { scores, cardsLeft } = computeRoundScores(state);
  state.scores = scores;
  state.cardsLeft = cardsLeft;
  if (state.players.length === 1) {
    state.phase = "game-over";
    return;
  }
  state.phase = "round-over";
  events.push({ type: "round-over", scores, cardsLeft });
}

/**
 * Turns the Swap Cards fired this turn into the decision that holds the turn
 * open, and reports whether it did. Called at the top of `endTurn`, which stops
 * there when this returns true: the turn is not counted and the seat does not
 * move until `applySwapChoice` has spent every swap.
 *
 * The owed count is zeroed either way, which is what keeps it turn-scoped. A
 * swap is FORFEIT rather than parked in two cases, both of which make the
 * choice meaningless: a player who has just gone out takes no more part in the
 * round, so there is nobody to ask; and a table with fewer than two piles
 * showing a card has no pair to swap.
 *
 * Because it lives in `endTurn`, every way a turn can end reaches it — including
 * the off-by-one Match bonus, whose pause ends by calling `endTurn` and so parks
 * here if one of the bonus cards was a Swap.
 */
function parkForOwedSwaps(state: GameState, events: EngineEvent[]): boolean {
  const owed = state.swapsOwed ?? 0;
  if (owed <= 0) return false;
  state.swapsOwed = 0;
  const player = currentPlayer(state);
  if (player.deck.length === 0 || legalSwapStackIndices(state).length < 2) return false;
  state.phase = "awaiting-swap-choice";
  state.pendingDecision = { type: "swap-top-cards", playerId: player.id, remainingSwaps: owed };
  // The turn will END in a later request, whose events start empty — so whether
  // this turn took a pile has to be carried across the pause, or a Swap drawn as
  // the refill after a take would score the take as a clean turn.
  state.swapTurnTookPile = events.some((e) => e.type === "stack-taken" && e.playerId === player.id);
  events.push({ type: "swap-choice-pending", playerId: player.id, remainingSwaps: owed });
  return true;
}

function endTurn(state: GameState, events: EngineEvent[]): void {
  if (state.phase !== "playing") return; // game/round already ended mid-resolution
  if (parkForOwedSwaps(state, events)) return; // the turn resumes, and ends, in applySwapChoice
  // Constellations, then the Mercy Rule, then any events the burst's own cards
  // charged - all out of one per-turn budget, so a burst can still fire an
  // event but a turn never fires more than MAX_STARFALL_EVENTS_PER_TURN.
  const fired = resolveStarfallTurnEnd(state, events);
  if (resolveMercy(state, events)) fireStarfallEvents(state, events, MAX_STARFALL_EVENTS_PER_TURN - fired);
  const finished = currentPlayer(state);
  finished.turnsTaken = (finished.turnsTaken ?? 0) + 1;
  scoreStreak(state, finished, events);
  scoreDrought(state, finished, events);
  if (state.swapTurnTookPile !== undefined) state.swapTurnTookPile = false;
  const index = nextSeat(state);
  if (index === null) {
    finishRound(state, events);
    return;
  }
  state.currentPlayerIndex = index;
  const next = currentPlayer(state);
  events.push({
    type: "turn-ended",
    playerId: finished.id,
    nextPlayerId: next.id,
  });
  // After the hand-over event, so a client animating the batch shows the table
  // pass to this player and THEN watches the blocks land on it.
  applyStreakLocksForTurn(state, next, events);
  burnDeckForTurn(state, next, events);
}

/**
 * Burning Deck (see `engine/burningDeck.ts`): the player whose turn is
 * beginning, on a streak that has stayed lit to the seventh turn and every other
 * one after, loses their top card.
 *
 * It runs LAST in `endTurn`: after Mercy and Constellations have had their say
 * at the end of the previous turn, and after the locks for this turn are chosen,
 * so what burns is the deck the player will actually play from. The card is
 * dropped, not played - it touches no pile, so a wild on top fires nothing and
 * nothing is charged or won. A Peek that was showing it stays up and now shows
 * the card that took its place: a Peek lasts until its player's next move, and
 * only that move clears it.
 *
 * A deck of one is left alone, so a burn can never put a player out.
 */
function burnDeckForTurn(state: GameState, next: PlayerState, events: EngineEvent[]): void {
  const burning = activeBurningDeck(state);
  if (!burning || !isBurnStreak(next.streak) || !canBurnFrom(next)) return;
  const card = next.deck.shift()!;
  burning.burned[next.id] = (burning.burned[next.id] ?? 0) + 1;
  events.push({ type: "deck-burn", playerId: next.id, card });
}

/**
 * Jourdan's Streaks: the finished turn either extends this player's run or ends
 * it, judged on one thing — whether they took a pile.
 *
 * Read off the events rather than from a flag set where the pile is taken,
 * because "did this turn take a pile" is a question about the whole turn, and a
 * turn is several engine steps long. The events ARE that record, and they are
 * already threaded through every path that can end one.
 *
 * The counter only moves while the rule is IN FORCE, which is the rule plus a
 * table big enough for it (`streaksApply`) — so a solo run, like a table playing
 * without it, publishes zeroes rather than a number nobody can spend. That is
 * what lets every client draw fire from the streak alone, without re-checking
 * the rule: a deck alight is always a deck with a move to make.
 */
function scoreStreak(state: GameState, player: PlayerState, events: EngineEvent[]): void {
  if (!streaksApply(normalizeHouseRules(state.houseRules), state.players.length)) return;
  // A player who has just gone out takes no further turn, so a streak ending
  // here could never be spent — and the last lap is long enough for the table to
  // sit looking at an empty deck on fire, which is a promise nobody can keep.
  if (player.deck.length === 0) return;
  const tookAPile = !!state.swapTurnTookPile || events.some((e) => e.type === "stack-taken" && e.playerId === player.id);
  player.streak = tookAPile ? 0 : (player.streak ?? 0) + 1;
}

/**
 * Drought: a turn that took no pile ends a run of misses. The run itself is
 * counted where the pile is taken (`takeStack`), so this only ever clears it -
 * and never clears an ARMED mist, which waits for the next miss however many
 * clean turns come first. A player who has gone out keeps no mist: there is no
 * miss left for them to spend it on.
 */
function scoreDrought(state: GameState, player: PlayerState, events: EngineEvent[]): void {
  if (!activeDrought(state)) return;
  if (player.deck.length === 0) {
    player.drought = 0;
    return;
  }
  const tookAPile = !!state.swapTurnTookPile || events.some((e) => e.type === "stack-taken" && e.playerId === player.id);
  if (!tookAPile && !isMisted(player.drought)) player.drought = 0;
}

/**
 * Hands the incoming player whatever the streaks spent on them are worth, and
 * clears the table of everybody else's.
 *
 * Locks live for exactly one turn - the turn of the player they were aimed at -
 * so the whole list is rebuilt here rather than added to and expired. That is
 * what makes "once they have had their turn, all locks are removed" a property
 * of the state rather than a cleanup somebody has to remember.
 *
 * Which piles shut is decided HERE, not when the streak was spent: a mark set
 * two laps ago would otherwise close piles chosen against a table that no longer
 * exists. The smallest piles are the ones that shut, so the choice is always
 * made against the board the player is actually about to face.
 */
function applyStreakLocksForTurn(state: GameState, next: PlayerState, events: EngineEvent[]): void {
  state.streakLocks = [];
  const targets = state.streakTargets ?? [];
  const target = targets.find((t) => t.playerId === next.id);
  state.streakTargets = targets;
  if (!target) return;
  // Spent: the mark is paid off by this turn whatever happens in it.
  state.streakTargets = targets.filter((t) => t.playerId !== next.id);
  const stackIndices = smallestOpenStackIndices(
    state.stacks.map((st) => st.cards.length),
    [],
    Math.min(target.lockCount, MAX_STREAK_LOCKS),
  );
  state.streakLocks = stackIndices.map((stackIndex) => ({ playerId: next.id, stackIndex }));
  events.push({ type: "streak-locks", playerId: next.id, stackIndices });
}

/**
 * Whether a guess card just drawn was its player's last, and if so when: the
 * round and the turns that player has finished in it. The last two only seed
 * the card's suspense plan (engine/suspense.ts), so the same card on the same
 * pile is not the same plan the next time round. Mirrored by lastCardStamp in
 * moves.go.
 */
function lastCardStamp(state: GameState, player: PlayerState): { lastCard: boolean; turn?: number; round?: number } {
  if (player.deck.length !== 0) return { lastCard: false };
  return { lastCard: true, turn: player.turnsTaken ?? 0, round: state.roundNumber };
}

/** Higher/Lower resolution: handles the duplicate-guess streak and the chaining bonus. */
function resolveHigherLower(
  state: GameState,
  player: PlayerState,
  stackIndex: StackIndex,
  guess: "higher" | "lower",
  events: EngineEvent[],
): void {
  const stack = state.stacks[stackIndex];
  const initialTop = topOf(stack) as ValueCard;
  let runningValue = initialTop.value;
  let duplicateMode = true;

  while (true) {
    const card = drawTop(player);

    if (isWild(card)) {
      placeOnStack(stack, card);
      events.push({ type: "wild-played", playerId: player.id, stackIndex, card });
      resolveWild(state, player, stackIndex, card, events);
      checkWentOut(state, player, events);
      endTurn(state, events);
      return;
    }

    if (duplicateMode && card.value === runningValue) {
      placeOnStack(stack, card);
      events.push({ type: "card-played", playerId: player.id, stackIndex, card, guess, free: false, duplicate: true, ...lastCardStamp(state, player) });
      chargeStarfall(state, player.id, events);
      if (checkWentOut(state, player, events)) {
        endTurn(state, events);
        return;
      }
      continue; // keep drawing with the same guess until a non-duplicate appears
    }
    duplicateMode = false;

    placeOnStack(stack, card);
    events.push({ type: "card-played", playerId: player.id, stackIndex, card, guess, free: false, duplicate: false, ...lastCardStamp(state, player) });

    const correct = guess === "higher" ? card.value > runningValue : card.value < runningValue;
    events.push({ type: "guess-result", playerId: player.id, stackIndex, correct });

    // A wrong guess is resolved BEFORE any win check: the played card goes back
    // into the player's deck with the rest of the stack, so playing your last
    // card on a bad guess refills your deck and the game continues. The refill
    // placement that follows draws from that deck, so the win check belongs
    // after it — it cannot actually fire (see refillTakenStack), but the
    // "check after every card leaves a deck" invariant is not worth an exception.
    if (!correct) {
      takeStack(state, player, stackIndex, events);
      refillTakenStack(state, player, stackIndex, events);
      checkWentOut(state, player, events);
      endTurn(state, events);
      return;
    }
    chargeStarfall(state, player.id, events);
    if (checkWentOut(state, player, events)) {
      endTurn(state, events);
      return;
    }

    const diff = Math.abs(card.value - runningValue);
    runningValue = card.value;
    // A wild or an empty deck stops the turn dead; otherwise a Bounty Pile pays
    // once the whole run - duplicates, chain and all - has resolved.
    const stopped = diff === 1 && resolveChainBonus(state, player, stackIndex, guess, runningValue, events);
    if (!stopped) payBounty(state, player, stackIndex, guess, events);
    endTurn(state, events);
    return;
  }
}

/**
 * Plays out a +/-1 chain of free bonus cards. Does NOT end the turn - the caller
 * does, once it has paid anything still owed on top (a Bounty Pile's card).
 * Returns true when the run STOPPED the turn - a wild fired, or the deck ran
 * out - and nothing more may be laid.
 */
function resolveChainBonus(
  state: GameState,
  player: PlayerState,
  stackIndex: StackIndex,
  guess: "higher" | "lower",
  startValue: number,
  events: EngineEvent[],
): boolean {
  const stack = state.stacks[stackIndex];
  let lastValue = startValue;
  while (true) {
    // No stop at 13 or 1: a card correct by one there still earns its free card,
    // which can never continue the run (nothing is 14 or 0), so it stays and
    // ends the chain like any other.
    if (player.deck.length === 0) break;

    const card = drawTop(player);
    placeOnStack(stack, card);

    if (isWild(card)) {
      events.push({ type: "wild-played", playerId: player.id, stackIndex, card });
      resolveWild(state, player, stackIndex, card, events);
      checkWentOut(state, player, events);
      return true;
    }

    const expected = guess === "higher" ? lastValue + 1 : lastValue - 1;
    const continuesChain = card.value === expected;
    events.push({ type: "chain-bonus-card", playerId: player.id, stackIndex, card, continuesChain });
    chargeStarfall(state, player.id, events);
    if (checkWentOut(state, player, events)) return true;

    if (!continuesChain) break;
    lastValue = card.value;
  }
  return player.deck.length === 0;
}

/**
 * Starfall's Bounty Pile: one free card on the bounty pile, for a correct Higher
 * or Lower there (and a Match off by two). Nothing when this is not the bounty
 * pile, or the deck is empty.
 *
 * It is a bonus card like any other - never evaluated, so it always stays - and
 * after a Higher or Lower it CONTINUES the chain when it lands exactly one on
 * from the card beneath it, in the guess's direction. `guess` is null for the
 * Match, which has no direction to chain in.
 */
function payBounty(
  state: GameState,
  player: PlayerState,
  stackIndex: StackIndex,
  guess: "higher" | "lower" | null,
  events: EngineEvent[],
): void {
  if (!isBountyStack(state, stackIndex) || player.deck.length === 0) return;
  const stack = state.stacks[stackIndex];
  const beneath = topOf(stack);
  const card = drawTop(player);
  placeOnStack(stack, card);
  const continuesChain =
    guess !== null &&
    beneath !== undefined &&
    !isWild(beneath) &&
    !isWild(card) &&
    card.value === (guess === "higher" ? beneath.value + 1 : beneath.value - 1);
  events.push({ type: "bounty-bonus-card", playerId: player.id, stackIndex, card, continuesChain });
  if (isWild(card)) {
    resolveWild(state, player, stackIndex, card, events);
    checkWentOut(state, player, events);
    return;
  }
  chargeStarfall(state, player.id, events);
  if (checkWentOut(state, player, events)) return;
  if (continuesChain && guess !== null) resolveChainBonus(state, player, stackIndex, guess, card.value, events);
}

/**
 * The last step of a turn at a Starfall table, once everything else in it has
 * resolved (owed swaps included - `endTurn` parks for those first).
 *
 * The turn just played spends one of the Bounty Pile's turns, then every full
 * meter fires an event, up to `MAX_STARFALL_EVENTS_PER_TURN`. It happens inside
 * the turn that filled the meter so that nothing changes a deck between its
 * owner's turns (Peek depends on that), and before the next player's locks are
 * chosen, so those are chosen against the table the event left.
 *
 * Returns how many events fired, so a Mercy burst after it spends what is left
 * of the turn's budget rather than a fresh one.
 */
function resolveStarfallTurnEnd(state: GameState, events: EngineEvent[]): number {
  const sf = activeStarfall(state);
  if (!sf) return 0;
  if (sf.bounty) {
    sf.bounty.turnsLeft--;
    if (sf.bounty.turnsLeft <= 0) {
      events.push({ type: "bounty-ended", stackIndices: sf.bounty.stackIndices });
      sf.bounty = null;
    }
  }
  return fireStarfallEvents(state, events, MAX_STARFALL_EVENTS_PER_TURN);
}

/**
 * Fires an event for every full meter, at most `budget` of them, and returns
 * how many fired. Stars past the budget wait on the meter for the next turn.
 */
function fireStarfallEvents(state: GameState, events: EngineEvent[], budget: number): number {
  const sf = activeStarfall(state);
  if (!sf) return 0;
  const player = currentPlayer(state);
  let fired = 0;
  for (; fired < budget && sf.stars >= STARFALL_METER_SIZE; fired++) {
    sf.stars -= STARFALL_METER_SIZE;
    const event = drawStarfallEvent(sf, enabledStarfallEvents(normalizeHouseRules(state.houseRules)));
    events.push({ type: "starfall-event", playerId: player.id, event, stars: sf.stars });
    switch (event) {
      case "comets":
        fireComets(state, sf, events);
        break;
      case "storm": {
        const shape = redealStorm(sf, state.stacks);
        events.push({ type: "card-storm", playerId: player.id, shape, stacks: stormLayout(state.stacks) });
        rebaseStarfall(state);
        break;
      }
      case "meteors": {
        const strikes = meteorStrike(sf, state.stacks);
        events.push({ type: "meteor-strike", playerId: player.id, strikes });
        rebaseStarfall(state);
        break;
      }
      case "swarm":
        fireSwarm(state, sf, events);
        break;
      case "bounty": {
        const stackIndices = pickBountyStacks(sf, state.stacks);
        const turns = bountyTurns(state);
        sf.bounty = { stackIndices, turnsLeft: turns };
        events.push({ type: "bounty-set", playerId: player.id, stackIndices: stackIndices.slice(), turns });
        break;
      }
    }
  }
  return fired;
}

/**
 * The Mercy Rule (see `engine/mercy.ts`): a player ending a turn at least half
 * a deal (`mercyGap`) ahead of the average opponent still holding cards bursts
 * a third of that gap onto random piles.
 * Asked of every turn, whether or not it took a pile, after Constellations has
 * had its say - except while the player rests after a burst (`mercyRest`, which
 * each of their turns counts down) and once anybody at the table is out, or a
 * party's last lap has reached it. Reports whether a burst happened, so
 * `endTurn` knows to look at the meter again.
 *
 * The whole burst leaves the deck at once (`mercy-burst`, naming every card and
 * where it is going), then lands one card at a time (`mercy-card`), each played
 * for real the way a comet is: a wild fires and takes its follow-up, a value
 * card can light a star. Taking them all first means a wild's follow-up draws
 * from the deck BEHIND the burst, and every card the burst named lands even if
 * a follow-up empties the deck and puts the player out - which only a wild can
 * do: the burst itself always leaves a card. A Swap among them is forfeit, as a
 * comet's is, because the turn's swaps have already been made.
 *
 * The cards come off the TOP, so a Peek shows the card that is next after
 * them, which is the promise a Peek makes.
 */
function resolveMercy(state: GameState, events: EngineEvent[]): boolean {
  const m = state.mercy;
  if (!m || !mercyApplies(normalizeHouseRules(state.houseRules), state.players.length)) return false;
  const player = currentPlayer(state);
  if ((player.mercyRest ?? 0) > 0) {
    player.mercyRest = (player.mercyRest ?? 0) - 1;
    return false;
  }
  if (state.winnerPlayerId !== null || (state.partyLastLapTurns ?? 0) > 0) return false;
  const others = state.players.filter((p) => p.id !== player.id && p.deck.length > 0).map((p) => p.deck.length);
  // A round saved before the deal was recorded has no `dealt`: 0, which never bursts.
  const count = mercyBurstSize(player.deck.length, others, m.dealt ?? 0);
  if (count === 0) return false;
  player.mercyRest = MERCY_COOLDOWN_TURNS;
  const mercyCards = mercyPiles(m, count).map((stackIndex) => ({ stackIndex, card: drawTop(player) }));
  events.push({ type: "mercy-burst", playerId: player.id, mercyCards });
  const firstEvent = events.length;
  for (const { stackIndex, card } of mercyCards) {
    placeOnStack(state.stacks[stackIndex], card);
    events.push({ type: "mercy-card", playerId: player.id, stackIndex, card });
    if (isWild(card)) resolveWild(state, player, stackIndex, card, events);
    else chargeStarfall(state, player.id, events);
  }
  checkWentOut(state, player, events);
  state.swapsOwed = 0;
  for (let i = events.length - 1; i >= firstEvent; i--) {
    if (events[i].type === "swap-owed") events.splice(i, 1);
  }
  return true;
}

/**
 * Comet Shower: the next `COMET_CARDS` cards of the player whose turn filled the
 * meter streak onto that many different random piles, unevaluated.
 *
 * A player already out passes it to the next player in seat order still holding
 * cards - even one with no turn left this round, since this is not a turn. A
 * wild fires and takes its follow-up as usual; a Swap among them is forfeit,
 * because the turn's swaps have already been made. Emptying the deck this way
 * goes out, as the cards stay on the table.
 */
function fireComets(state: GameState, sf: StarfallState, events: EngineEvent[]): void {
  const seats = state.players.length;
  let target: PlayerState | null = null;
  for (let i = 0; i < seats; i++) {
    const p = state.players[(state.currentPlayerIndex + i) % seats];
    if (p.deck.length > 0) {
      target = p;
      break;
    }
  }
  if (!target) return;
  // A shower passed to another seat draws from their deck between their turns.
  // Their Peek is left up: it lasts until that player's next move, and shows
  // the card that is next after the comets.
  const firstEvent = events.length;
  const piles = starfallShuffle(sf, STACK_INDICES).slice(0, COMET_CARDS);
  for (const stackIndex of piles) {
    if (target.deck.length === 0) break;
    const card = drawTop(target);
    placeOnStack(state.stacks[stackIndex], card);
    events.push({ type: "comet-card", playerId: target.id, stackIndex, card });
    if (isWild(card)) resolveWild(state, target, stackIndex, card, events);
    else chargeStarfall(state, target.id, events);
    if (checkWentOut(state, target, events)) break;
  }
  // A Swap among the comets is forfeit - the turn's swaps have already been
  // made - so its callout is taken back too, or the table is told to expect a
  // swap that never comes.
  state.swapsOwed = 0;
  for (let i = events.length - 1; i >= firstEvent; i--) {
    if (events[i].type === "swap-owed") events.splice(i, 1);
  }
}

/**
 * Meteor Swarm: the top card of the player whose turn filled the meter leaves
 * their deck and shatters - it goes to no pile, it is simply gone, which is a
 * card closer to going out - and its `SWARM_SHARDS` shards hit the table as
 * small meteors (`meteorSwarm`).
 *
 * Who gives the card follows the Comet Shower: a player already out passes it
 * to the next in seat order still holding cards. Nobody holding any still
 * fires the shards, from nowhere. A Peek on the giver stays up and shows the
 * card now on top, since a Peek lasts until its player's next move. Emptying a
 * deck this way goes out.
 */
function fireSwarm(state: GameState, sf: StarfallState, events: EngineEvent[]): void {
  const seats = state.players.length;
  let giver: PlayerState | null = null;
  for (let i = 0; i < seats; i++) {
    const p = state.players[(state.currentPlayerIndex + i) % seats];
    if (p.deck.length > 0) {
      giver = p;
      break;
    }
  }
  let card: Card | null = null;
  if (giver) {
    card = drawTop(giver);
  }
  const strikes = meteorSwarm(sf, state.stacks);
  events.push({ type: "meteor-swarm", playerId: (giver ?? currentPlayer(state)).id, card, strikes });
  rebaseStarfall(state);
  if (giver) checkWentOut(state, giver, events);
}

function resolveMatch(state: GameState, player: PlayerState, stackIndex: StackIndex, events: EngineEvent[]): void {
  const stack = state.stacks[stackIndex];
  const topCard = topOf(stack) as ValueCard;
  const card = drawTop(player);
  placeOnStack(stack, card);

  if (isWild(card)) {
    events.push({ type: "wild-played", playerId: player.id, stackIndex, card });
    resolveWild(state, player, stackIndex, card, events);
    checkWentOut(state, player, events);
    endTurn(state, events);
    return;
  }

  events.push({ type: "card-played", playerId: player.id, stackIndex, card, guess: "match", free: false, duplicate: false, ...lastCardStamp(state, player) });

  // Round the ring: a 13 on a 1 is off by one (`matchDistance`).
  const off = matchDistance(card.value, topCard.value);
  // Only a card that STAYS can light a star: a failed Match is taken straight back.
  if (off <= 2) chargeStarfall(state, player.id, events);
  const bounty = isBountyStack(state, stackIndex);
  // The win check only runs in branches where the played card actually stays
  // (exact, off-by-one, off-by-two). A failed match takes the stack back into
  // the deck, so an empty deck at that moment is not a win.

  if (off === 0) {
    if (checkWentOut(state, player, events)) {
      endTurn(state, events);
      return;
    }
    events.push(
      bounty
        ? { type: "five-out", playerId: player.id, originStackIndex: stackIndex, bounty: true }
        : { type: "five-out", playerId: player.id, originStackIndex: stackIndex },
    );
    // On the Bounty Pile every other pile takes two cards: the pass runs twice.
    passes: for (let pass = 0; pass < (bounty ? 2 : 1); pass++) {
      for (const idx of STACK_INDICES) {
        if (idx === stackIndex) continue;
        if (player.deck.length === 0) break passes;
        const bonus = drawTop(player);
        placeOnStack(state.stacks[idx], bonus); // Five Out bonus cards are never evaluated
        events.push({ type: "five-out-bonus-card", playerId: player.id, stackIndex: idx, card: bonus });
        if (isWild(bonus)) resolveWild(state, player, idx, bonus, events); // wilds fire no matter how they're played
        else chargeStarfall(state, player.id, events);
        if (checkWentOut(state, player, events)) break passes;
      }
    }
    endTurn(state, events);
    return;
  }

  if (off === 1) {
    if (checkWentOut(state, player, events)) {
      endTurn(state, events);
      return;
    }
    const placements = bounty ? 4 : 2; // the Bounty Pile doubles it
    events.push(
      bounty
        ? { type: "match-off-by-one", playerId: player.id, originStackIndex: stackIndex, placements }
        : { type: "match-off-by-one", playerId: player.id, originStackIndex: stackIndex },
    );
    state.phase = "awaiting-bonus-choice";
    state.pendingDecision = {
      type: "match-off-by-one-bonus",
      playerId: player.id,
      originStackIndex: stackIndex,
      remainingPlacements: placements,
    };
    return; // caller must supply the bonus stack choices via applyBonusStackChoice
  }

  if (off === 2) {
    if (checkWentOut(state, player, events)) {
      endTurn(state, events);
      return;
    }
    events.push({ type: "match-off-by-two", playerId: player.id, stackIndex });
    payBounty(state, player, stackIndex, null, events);
    endTurn(state, events);
    return;
  }

  events.push({ type: "match-fail", playerId: player.id, stackIndex });
  takeStack(state, player, stackIndex, events);
  refillTakenStack(state, player, stackIndex, events);
  checkWentOut(state, player, events);
  endTurn(state, events);
}

/**
 * Tells one party table the turn count its round has been fixed at by a player
 * going out somewhere in the party, and ends the round on the spot if this table
 * has already had those turns. Mutates `state` in place and reports whether
 * anything changed, with the events the change produced.
 *
 * This is the second place a round can end, and the only one that is not
 * `endTurn`: the news arrives BETWEEN turns rather than during one, and a table
 * whose seats are all level with — or past — the number has nothing left to
 * play. Waiting for its current player to finish would hand them a turn more
 * than the player who went out, which is the one thing the equal-turns rule
 * exists to prevent. A move is applied whole in a single request, so there is
 * never half a turn to abandon; a pending bonus or swap choice is the exception,
 * and either phase is left alone to resolve into its own `endTurn`.
 *
 * Idempotent, and never lowers the number: the first player out anywhere fixes
 * the round, and the server re-sends the push every time a table folds, so
 * landing twice has to be free. A table whose round is already over is refused
 * outright rather than merely left alone — the push goes to the table that FIXED
 * the round as well as to the others, and recording a number on a finished round
 * would write the document and publish a snapshot to say nothing.
 *
 * Mirrors `ApplyPartyLastLap` in the Go engine, which is the copy that actually
 * runs — party mode is online only (docs/PARTY_MODE.md). It lives here rather
 * than in `engine/party.ts` because it is the turn rule, and `nextSeat` and
 * `finishRound` are.
 */
export function applyPartyLastLap(state: GameState, turns: number): { changed: boolean; events: EngineEvent[] } {
  if (turns <= 0 || (state.partyLastLapTurns ?? 0) >= turns) return { changed: false, events: [] };
  if (state.phase === "round-over" || state.phase === "game-over") return { changed: false, events: [] };
  state.partyLastLapTurns = turns;
  if (state.phase !== "playing") return { changed: true, events: [] };
  const events: EngineEvent[] = [];
  if (nextSeat(state) === null) finishRound(state, events);
  return { changed: true, events };
}

/**
 * The turn count a finishing table fixes its party's round at: the turns the
 * player who went out here had taken, which is the most anyone at this table
 * has.
 *
 * Zero when nobody here is out — a table that ended only because another one
 * fixed the round does not get to re-fix it, and neither does one still
 * mid-round. Mirrors `PartyLastLapTurnsFor` in the Go engine.
 */
export function partyLastLapTurnsFor(state: GameState): number {
  if (state.winnerPlayerId === null) return 0;
  return Math.max(0, ...state.players.map((p) => p.turnsTaken ?? 0));
}

/** The smallest table a round can carry on at; below it the round is solo. */
export const MIN_SEATS_MID_ROUND = 2;

/**
 * Takes a seat off the table, cards and all, and hands the turn on if it was
 * theirs. Mutates `state` in place and returns whatever the removal announced.
 *
 * This is how an online host clears out somebody who has left. The seat goes
 * entirely rather than being flagged, because every rule in both engines reads
 * the roster as "everyone holding cards" — the same reason a mid-round joiner
 * waits off the roster rather than sitting on it with a flag. A seat kept and
 * skipped would be a player who takes turns nobody can play, wins a round they
 * are not in, and scores a point for it.
 *
 * Their cards leave play with them. There is no draw pile to return them to and
 * no rule counts the cards on the table, so a round simply carries on with
 * fewer — the only reading that hands the remaining players neither a windfall
 * nor a punishment for something they did not do.
 *
 * The round can END here, and that is not an edge case: the seat removed may
 * have been the last one still owed a turn. It is the third and last place a
 * round ends, alongside `endTurn` and `applyPartyLastLap`.
 *
 * Mirrors `RemovePlayer` in the Go engine, which is the copy that actually runs
 * — removing a player is an online, host-only action. Two things there have no
 * counterpart here because they are not rules: the refusal to remove the HOST,
 * and the waiting list a mid-round joiner sits on. Both are the server's, and
 * both live in `Manager.KickPlayer` and `engine.RemovePartyMember`.
 */
export function removePlayer(state: GameState, playerId: string): { events: EngineEvent[]; removed: boolean } {
  const index = state.players.findIndex((p) => p.id === playerId);
  if (index < 0) return { events: [], removed: false };
  const midRound = isTurnInProgress(state.phase);
  if (midRound && state.players.length - 1 < MIN_SEATS_MID_ROUND) return { events: [], removed: false };

  const wasCurrent = index === state.currentPlayerIndex;
  state.players.splice(index, 1);
  // The marks aimed at them and the piles their own spent streak is holding shut
  // both wait on a turn that is never coming, and a lock nobody can release
  // would shut a pile for the rest of the round.
  //
  // Who went out is deliberately NOT touched. `winnerPlayerId` is what
  // `nextSeat` reads to know the round is in its last lap, so clearing it
  // because the player who fixed the round has left would UN-fix it — the table
  // would drop out of its final turns and carry on playing a round already
  // decided. A departed id left in the winners list draws nobody: every screen
  // renders the round off `players`.
  state.streakLocks = (state.streakLocks ?? []).filter((l) => l.playerId !== playerId);
  state.streakTargets = (state.streakTargets ?? []).filter((t) => t.playerId !== playerId);

  if (!midRound) {
    if (state.currentPlayerIndex >= state.players.length) state.currentPlayerIndex = 0;
    return { events: [], removed: true };
  }

  // A bonus or swap choice belonging to the seat that just left is owed by
  // nobody, and the table cannot move until it is gone. The swaps go with it:
  // they were the leaver's to make.
  if (state.pendingDecision?.playerId === playerId) {
    state.pendingDecision = null;
    state.phase = "playing";
    state.swapsOwed = 0;
    if (state.swapTurnTookPile !== undefined) state.swapTurnTookPile = false;
  }

  const events: EngineEvent[] = [];
  if (!wasCurrent) {
    if (index < state.currentPlayerIndex) state.currentPlayerIndex--;
    return { events, removed: true };
  }

  // Aiming `nextSeat` at the seat BEFORE the hole makes it hand the turn to
  // whoever was sitting after them, which is the order the table would have
  // taken anyway.
  state.currentPlayerIndex = (index - 1 + state.players.length) % state.players.length;
  const next = nextSeat(state);
  if (next === null) {
    finishRound(state, events);
    return { events, removed: true };
  }
  state.currentPlayerIndex = next;
  // The same call `endTurn` makes, for the same reason: a mark waiting for this
  // player's turn to come round is paid off by the turn they are now getting,
  // and skipping it would leave the piles open and the mark standing.
  applyStreakLocksForTurn(state, state.players[next], events);
  return { events, removed: true };
}

export function applyMove(state: GameState, playerId: string, move: PlayerMove): ApplyMoveResult {
  if (state.phase !== "playing") {
    throw new IllegalMoveError(`cannot apply a move while game phase is "${state.phase}"`);
  }
  const next = clone(state);
  const player = currentPlayer(next);
  if (player.id !== playerId) {
    throw new IllegalMoveError(`it is not player ${playerId}'s turn`);
  }
  if (player.deck.length === 0) {
    throw new IllegalMoveError(`player ${playerId} has no cards left`);
  }
  // Before a single card is drawn: the peek showed the card this move is about
  // to play, and it has done its job whatever the move turns out to be. Clearing
  // it here rather than once that card is played keeps it a property of whose
  // move it is, not of which of a dozen resolution paths happened to draw.
  if (player.peeking) player.peeking = false;

  if (!legalStackIndicesForTurn(next).includes(move.stackIndex)) {
    // Two rules can shut a pile, and they are told apart by which one actually
    // did it: a locked pile is locked for everyone, so that reading comes first.
    throw new IllegalMoveError(
      lockedStackIndices(next.streakLocks ?? []).includes(move.stackIndex)
        ? "that pile is locked by a streak"
        : "Vinny's Rule: your last card must go on the tallest pile",
    );
  }

  const stack = next.stacks[move.stackIndex];
  const events: EngineEvent[] = [];

  if (move.type === "free-place") {
    if (!stackAllowsFree(stack)) {
      throw new IllegalMoveError("free placement is only allowed on an empty stack or one topped with a wild card");
    }
    const card = drawTop(player);
    placeOnStack(stack, card);
    if (isWild(card)) {
      // A free-placed wild fires too (it may land on a bonus-placed wild or an empty stack).
      events.push({ type: "wild-played", playerId, stackIndex: move.stackIndex, card });
      resolveWild(next, player, move.stackIndex, card, events);
    } else {
      events.push({ type: "card-played", playerId, stackIndex: move.stackIndex, card, guess: null, free: true, duplicate: false });
      chargeStarfall(next, playerId, events);
    }
    checkWentOut(next, player, events);
    endTurn(next, events);
    next.version++;
    return { state: next, events };
  }

  // guess move
  if (stackAllowsFree(stack)) {
    throw new IllegalMoveError("this stack is empty or wild-topped; use a free placement instead of a guess");
  }
  const topCard = topOf(stack) as ValueCard;
  if ((move.guess === "higher" || move.guess === "lower") && isBoundaryInvalid(move.guess, topCard.value)) {
    throw new IllegalMoveError(`cannot guess ${move.guess} on a stack topped with ${topCard.value}`);
  }

  if (move.guess === "match") {
    resolveMatch(next, player, move.stackIndex, events);
  } else {
    resolveHigherLower(next, player, move.stackIndex, move.guess, events);
  }

  next.version++;
  return { state: next, events };
}

/** Applies one of the two player-chosen bonus placements after a Match-guess that was off by exactly 1. */
export function applyBonusStackChoice(state: GameState, playerId: string, choice: BonusStackChoice): ApplyMoveResult {
  const decision = state.pendingDecision;
  if (state.phase !== "awaiting-bonus-choice" || decision?.type !== "match-off-by-one-bonus") {
    throw new IllegalMoveError("no bonus placement is currently pending");
  }
  if (decision.playerId !== playerId) {
    throw new IllegalMoveError(`it is not player ${playerId}'s bonus placement`);
  }
  if (choice.stackIndex === decision.originStackIndex) {
    throw new IllegalMoveError("bonus cards must go on one of the other stacks");
  }

  const next = clone(state);
  const player = next.players.find((p) => p.id === playerId)!;
  const events: EngineEvent[] = [];

  const bonus = drawTop(player);
  placeOnStack(next.stacks[choice.stackIndex], bonus); // off-by-one bonus cards are never evaluated
  events.push({ type: "match-off-by-one-bonus-card", playerId, stackIndex: choice.stackIndex, card: bonus });
  if (isWild(bonus)) resolveWild(next, player, choice.stackIndex, bonus, events); // wilds fire no matter how they're played
  else chargeStarfall(next, playerId, events);

  // Going out abandons whatever is left of the bonus: there are no cards to
  // place. The phase goes back to "playing" first because that is what `endTurn`
  // will and must refuse to run without.
  if (checkWentOut(next, player, events)) {
    next.pendingDecision = null;
    next.phase = "playing";
    endTurn(next, events);
    next.version++;
    return { state: next, events };
  }

  next.pendingDecision = { ...decision, remainingPlacements: decision.remainingPlacements - 1 };
  if (next.pendingDecision.remainingPlacements <= 0) {
    next.pendingDecision = null;
    next.phase = "playing";
    endTurn(next, events);
  }

  next.version++;
  return { state: next, events };
}

/**
 * Makes one of the swaps a player's Swap Cards owed: the top cards of the two
 * chosen piles trade places, and when the last swap is made the turn ends.
 *
 * Any two piles with a card on them are legal, whatever else is shutting piles
 * this turn. A streak lock and Vinny's Rule both limit where a player may PLAY,
 * and a swap plays nothing — it rearranges the table the next player inherits,
 * and a locked pile is as much a part of that table as any other.
 *
 * Only the tops move. The counts stay where they were and the cards beneath are
 * untouched, so a swap can hand a tall pile a dangerous top card but never moves
 * the pile itself. Neither card is evaluated and neither fires — a wild moved on
 * top of a pile is simply a wild-topped pile, i.e. a free placement.
 *
 * Mirrors `ApplySwapChoice` in the Go engine.
 */
export function applySwapChoice(state: GameState, playerId: string, choice: SwapChoice): ApplyMoveResult {
  const decision = state.pendingDecision;
  if (state.phase !== "awaiting-swap-choice" || decision?.type !== "swap-top-cards") {
    throw new IllegalMoveError("no swap is currently pending");
  }
  if (decision.playerId !== playerId) {
    throw new IllegalMoveError(`it is not player ${playerId}'s swap`);
  }
  const indices = choice?.stackIndices;
  if (!Array.isArray(indices) || indices.length !== 2) {
    throw new IllegalMoveError("a swap names exactly two piles");
  }
  const [a, b] = indices;
  if (!STACK_INDICES.includes(a) || !STACK_INDICES.includes(b)) {
    throw new IllegalMoveError("a swap names two of the five piles");
  }
  if (a === b) {
    throw new IllegalMoveError("a swap needs two different piles");
  }
  if (state.stacks[a].cards.length === 0 || state.stacks[b].cards.length === 0) {
    throw new IllegalMoveError("both piles in a swap must have a card on top");
  }

  const next = clone(state);
  const events: EngineEvent[] = [];
  const cardA = next.stacks[a].cards[0];
  const cardB = next.stacks[b].cards[0];
  next.stacks[a].cards[0] = cardB;
  next.stacks[b].cards[0] = cardA;
  // The card under each swapped top rides on the event: online the client only
  // knows a pile's top and count, and the animation shows these face up under
  // the flying tops. Taken from the pile itself so local and online agree.
  const beneath = [next.stacks[a].cards[1] ?? null, next.stacks[b].cards[1] ?? null];
  events.push({ type: "stacks-swapped", playerId, stackIndices: [a, b], cards: [cardA, cardB], beneath });
  chargeStarfall(next, playerId, events);

  const remainingSwaps = decision.remainingSwaps - 1;
  if (remainingSwaps > 0 && legalSwapStackIndices(next).length >= 2) {
    next.pendingDecision = { ...decision, remainingSwaps };
  } else {
    // Back to "playing" first because that is what `endTurn` will and must
    // refuse to run without; `swapsOwed` is already 0, so it does not park again.
    next.pendingDecision = null;
    next.phase = "playing";
    endTurn(next, events);
  }

  next.version++;
  return { state: next, events };
}

/** The piles a swap may name: every pile with a card on top. Mirrors `LegalSwapStackIndices`. */
export function legalSwapStackIndices(state: GameState): StackIndex[] {
  return STACK_INDICES.filter((i) => state.stacks[i].cards.length > 0);
}

/**
 * A uniformly random legal swap, for a player who cannot or will not choose:
 * the turn timer running out, or a disconnected seat being auto-played. Every
 * unordered pair is equally likely — pairs are listed with the lower index
 * first, in ascending order, and one is drawn with a single `nextInt`.
 * Mirrors `RandomSwapChoice` in the Go engine.
 */
export function randomSwapChoice(state: GameState, rng: RNG): SwapChoice {
  const pairs = legalSwapPairs(state);
  if (pairs.length === 0) throw new IllegalMoveError("there are not two piles to swap");
  return { stackIndices: pairs[rng.nextInt(pairs.length)] };
}

/** Every legal swap as an unordered pair, lower index first, in ascending order. */
export function legalSwapPairs(state: GameState): [StackIndex, StackIndex][] {
  const legal = legalSwapStackIndices(state);
  const pairs: [StackIndex, StackIndex][] = [];
  for (let i = 0; i < legal.length; i++) {
    for (let j = i + 1; j < legal.length; j++) pairs.push([legal[i], legal[j]]);
  }
  return pairs;
}

/**
 * The card a Peek is showing this player, or null when there is none to show.
 *
 * It is their own `deck[0]`, and it is shown only once the turn the Peek fired
 * in is over: while that same player is still parked in a decision of their own
 * (a bonus placement, or the swaps the turn owes) the card stays hidden. Those
 * decisions are made against the table, and showing the card that lands next
 * would let the player place and swap with it in hand — the Peek is a look at
 * the NEXT turn, not a better answer to this one.
 *
 * This is the only deck card that is ever published (`PublicPlayerView.peekCard`),
 * which is why it has to be one function both engines agree on. Mirrors
 * `VisiblePeekCard` in the Go engine.
 */
export function visiblePeekCard(state: GameState, playerId: string): Card | null {
  const player = state.players.find((p) => p.id === playerId);
  if (!player?.peeking) return null;
  if (player.deck.length === 0) return null;
  if (state.pendingDecision && state.pendingDecision.playerId === playerId) return null;
  return player.deck[0];
}

/**
 * Which piles the player to act may target at all, before asking what action
 * the pile allows. Everything unless a house rule narrows it: a pile shut by
 * someone's spent streak, or — on the turn a player is down to their last card —
 * every pile but the tallest.
 *
 * Bonus placements are deliberately NOT filtered through this. An off-by-one
 * bonus already has to miss the pile it was earned on, and intersecting that
 * with the piles a turn may use can leave a player owing a placement they have
 * nowhere legal to make: four piles shut plus the origin excluded is a table
 * with no legal answer at all. A player at one card who earns the bonus has in
 * any case already out by the time it would be offered (`checkWentOut` runs
 * first, and the turn ends there).
 */
export function legalStackIndicesForTurn(state: GameState): StackIndex[] {
  const player = state.players[state.currentPlayerIndex];
  if (!player) return [...STACK_INDICES];
  return allowedStackIndices(
    normalizeHouseRules(state.houseRules),
    state.stacks.map((s) => s.cards.length),
    player.deck.length,
    lockedStackIndices(state.streakLocks ?? []),
  );
}

/**
 * Everyone a spent streak lands on, or a refusal saying why it cannot be spent.
 *
 * The two modes ask completely different questions and this is where they are
 * told apart, once, so that neither engine nor any client has the branch spread
 * through it. Aimed: is this a real opponent who can still take a mark? Blue
 * Shell: the spender does not choose at all — the fire goes to whoever is closest
 * to going out, other than the spender, and a spender who is out in front on
 * their own has nowhere to send it.
 *
 * Returns the target ids in seat order. Empty is never a success: a spend that
 * marks nobody would put the fire out for nothing, so every path that produces
 * one throws instead.
 */
function streakTargetIds(state: GameState, spenderId: string, chosenId: string | null): string[] {
  const rules = normalizeHouseRules(state.houseRules);
  const targets = state.streakTargets ?? [];

  if (!rules.blueShell) {
    if (!chosenId) throw new IllegalMoveError("a streak has to name the player it is spent on");
    if (chosenId === spenderId) {
      throw new IllegalMoveError("a streak is spent on somebody else, not on yourself");
    }
    const chosen = state.players.find((p) => p.id === chosenId);
    if (!chosen) {
      throw new IllegalMoveError(`player ${chosenId} is not at this table`);
    }
    // A player who is out never takes another turn, so the mark could not land:
    // the fire would be spent on nothing. Reachable only in the last lap, where
    // every table has a finished player sitting on it.
    if (chosen.deck.length === 0) {
      throw new IllegalMoveError(`player ${chosenId} is already out`);
    }
    if (!canTargetPlayer(targets, chosenId)) {
      throw new IllegalMoveError(`player ${chosenId} already has as many piles shut as the rule allows`);
    }
    return [chosenId];
  }

  // The rule reads as "the fire chases whoever is winning", and the spender is
  // the one player it can never help — so they come out of their own lead first.
  // A spender TIED at the front still has targets, the others level with them;
  // only a sole leader is left with nowhere to send it, and refusing is the whole
  // of it there: they hold the streak until somebody draws level or overtakes.
  const aimedAt = blueShellTargetIds(
    state.players.map((p) => ({ playerId: p.id, cardsLeft: p.deck.length })),
    spenderId,
  );
  if (aimedAt.length === 0) {
    throw new IllegalMoveError("Blue Shell: you are in the lead on your own, so your streak has nobody to aim at");
  }
  const open = aimedAt.filter((id) => canTargetPlayer(targets, id));
  if (open.length === 0) {
    throw new IllegalMoveError("Blue Shell: the lead already has as many piles shut as the rule allows");
  }
  return open;
}

/**
 * Jourdan's Streaks: the player to act spends their deck's fire on ONE opponent,
 * who starts their next turn with piles shut.
 *
 * A separate action rather than a variant of `applyMove`, because it is one:
 * the spender's turn has not been taken when this returns, and the very next
 * thing they do is play it — on a full table, since the cost lands on somebody
 * else. What stops it being free is that it is one shot, earned over five turns
 * of not taking a pile.
 *
 * Aimed rather than table-wide, so the fire is a decision: the table can see who
 * is close to going out, and the player marked can see it coming and spend the
 * turn in front of it getting ready.
 */
export function applyStreak(
  state: GameState,
  playerId: string,
  targetPlayerId: string | null = null,
): ApplyMoveResult {
  if (state.phase !== "playing") {
    throw new IllegalMoveError(`cannot use a streak while game phase is "${state.phase}"`);
  }
  const rules = normalizeHouseRules(state.houseRules);
  const next = clone(state);
  const player = currentPlayer(next);
  if (player.id !== playerId) {
    throw new IllegalMoveError(`it is not player ${playerId}'s turn`);
  }
  if (!canUseStreak(rules, player.streak ?? 0)) {
    throw new IllegalMoveError(`player ${playerId} cannot use a streak right now`);
  }
  if (!streakSpendApplies(rules, next.players.length)) {
    throw new IllegalMoveError("a streak needs another player to be aimed at");
  }

  const targetIds = streakTargetIds(next, playerId, targetPlayerId);
  const pending = next.streakTargets ?? [];
  // Each mark is counted on its own, so two players tied for the lead each take
  // a first-mark's worth rather than sharing one between them.
  const hits: StreakTarget[] = targetIds.map((id) => ({
    playerId: id,
    lockCount: locksAfterTargeting(pendingLockCount(pending, id)),
  }));
  next.streakTargets = [
    ...pending.filter((t) => !targetIds.includes(t.playerId)),
    ...hits,
  ];
  // The fire is spent whether or not it changes the outcome, and has to be
  // earned again from zero.
  player.streak = 0;

  const events: EngineEvent[] = [{ type: "streak-target", playerId, streakTargets: hits }];
  next.version++;
  return { state: next, events };
}

export function legalStackIndicesForFree(state: GameState): StackIndex[] {
  const allowed = legalStackIndicesForTurn(state);
  return allowed.filter((i) => stackAllowsFree(state.stacks[i]));
}

/**
 * The guesses this pile's top card admits. Answers "what can be done HERE",
 * not "may this player play here" — an empty result means free placement
 * applies, so callers that also have to respect `legalStackIndicesForTurn`
 * must ask it separately rather than reading emptiness as "not allowed".
 */
export function legalGuessesForStack(state: GameState, stackIndex: StackIndex): GuessType[] {
  const stack = state.stacks[stackIndex];
  if (stackAllowsFree(stack)) return [];
  const top = topOf(stack) as ValueCard;
  const guesses: GuessType[] = ["match"];
  if (top.value < 13) guesses.push("higher");
  if (top.value > 1) guesses.push("lower");
  return guesses;
}

export { stackAllowsFree, topOf };
